import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import NavigationBar from './NavigationBar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TaskManagement = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [projectsWithoutTasks, setProjectsWithoutTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('tasks');
  const [taskFilter, setTaskFilter] = useState('active');
  const [managerFilter, setManagerFilter] = useState('all');
  const [managers, setManagers] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskResult, setTaskResult] = useState('');
  const [nextTaskTitle, setNextTaskTitle] = useState('');
  const [nextTaskDeadline, setNextTaskDeadline] = useState('');

  useEffect(() => {
    loadTasks();
    loadManagers();
    if (user?.role === 'admin' || user?.role === 'lead') {
      loadProjectsWithoutTasks();
    }
  }, [user]);

  // Recalculate filtered tasks when filters change
  useEffect(() => {
    const filtered = getFilteredTasks();
    setFilteredTasks(filtered);
    console.log(`Filter applied: taskFilter=${taskFilter}, managerFilter=${managerFilter}, showing ${filtered.length} of ${tasks.length} tasks`);
  }, [tasks, taskFilter, managerFilter, user]);

  const loadTasks = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/tasks/my-tasks`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setTasks(response.data.tasks || []);
      
    } catch (error) {
      console.error('Failed to load tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadManagers = async () => {
    try {
      if (user?.role === 'admin' || user?.role === 'lead') {
        const response = await axios.get(`${API}/managers`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        
        // Backend returns array directly, not wrapped in {managers: [...]}
        setManagers(response.data || []);
        console.log('Loaded managers:', response.data);
      }
    } catch (error) {
      console.error('Failed to load managers:', error);
    }
  };

  const loadProjectsWithoutTasks = async () => {
    try {
      const response = await axios.get(`${API}/projects/without-tasks`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setProjectsWithoutTasks(response.data.projects || []);
    } catch (error) {
      console.error('Failed to load projects without tasks:', error);
    }
  };

  const openCompletionModal = (task) => {
    setSelectedTask(task);
    setTaskResult('');
    setNextTaskTitle('');
    setNextTaskDeadline('');
    setShowCompletionModal(true);
  };

  const completeTask = async () => {
    if (!taskResult.trim()) {
      alert('Необходимо обязательно указать результат выполнения задачи');
      return;
    }

    try {
      await axios.put(`${API}/tasks/${selectedTask.id}/complete`, {
        result: taskResult.trim(),
        next_task_title: nextTaskTitle.trim(),
        next_task_deadline: nextTaskDeadline
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      alert(`Задача "${selectedTask.title}" завершена! ${nextTaskTitle ? 'Создана следующая задача.' : ''}`);
      
      setShowCompletionModal(false);
      setSelectedTask(null);
      loadTasks(); // Reload tasks
      
    } catch (error) {
      console.error('Failed to complete task:', error);
      alert('Не удалось завершить задачу: ' + (error.response?.data?.detail || 'Неизвестная ошибка'));
    }
  };

  const getFilteredTasks = () => {
    let filtered = tasks;
    
    // Filter by completion status
    switch (taskFilter) {
      case 'active':
        filtered = filtered.filter(task => !task.completed);
        break;
      case 'completed':
        filtered = filtered.filter(task => task.completed);
        break;
      default:
        // All tasks
        break;
    }
    
    // Filter by manager (admin/lead only)
    if (user?.role === 'admin' || user?.role === 'lead') {
      switch (managerFilter) {
        case 'my':
          // Show only current user's tasks
          filtered = filtered.filter(task => 
            task.assigned_to === user.id ||
            task.project_assigned_manager === user.id
          );
          break;
        case 'all':
          // Show all tasks (no additional filtering)
          break;
        default:
          // Specific manager by ID
          filtered = filtered.filter(task => 
            task.assigned_to === managerFilter ||
            task.project_assigned_manager === managerFilter
          );
          break;
      }
    } else {
      // Managers see their own tasks + tasks from their projects
      filtered = filtered.filter(task => 
        task.assigned_to === user.id || 
        task.project_assigned_manager === user.id ||
        (!task.assigned_to && !task.project_assigned_manager)
      );
    }
    
    return filtered;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <NavigationBar />
      
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-white rounded-lg shadow-md p-8 mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              {user?.role === 'manager' ? 'Мои задачи' : 'Управление задачами'}
            </h1>

            {/* Section Navigation */}
            <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit mb-6">
              <button
                onClick={() => setActiveSection('tasks')}
                className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                  activeSection === 'tasks' ? 'bg-white text-blue-600 shadow' : 'text-gray-600'
                }`}
              >
                📋 {user?.role === 'manager' ? 'Мои задачи' : 'Задачи команды'}
              </button>
              
              {/* Overdue Tasks Section */}
              <button
                onClick={() => setActiveSection('overdue-tasks')}
                className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                  activeSection === 'overdue-tasks'
                    ? 'bg-white text-red-600 shadow' 
                    : 'text-gray-600'
                }`}
              >
                🚨 Просроченные ({tasks.filter(t => !t.completed && t.deadline && new Date(t.deadline) < new Date()).length})
              </button>
              
              {(user?.role === 'admin' || user?.role === 'lead') && (
                <button
                  onClick={() => setActiveSection('projects-without-tasks')}
                  className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                    activeSection === 'projects-without-tasks'
                      ? 'bg-white text-red-600 shadow' 
                      : 'text-gray-600'
                  }`}
                >
                  ⚠️ Проекты без задач ({projectsWithoutTasks.length})
                </button>
              )}
            </div>

            {/* Task Filters - only for tasks section */}
            {activeSection === 'tasks' && (
              <div className="space-y-4">
                {/* Task Status Filter */}
                <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
                  <button
                    onClick={() => setTaskFilter('active')}
                    className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                      taskFilter === 'active' ? 'bg-white text-blue-600 shadow' : 'text-gray-600'
                    }`}
                  >
                    Активные ({tasks.filter(t => !t.completed).length})
                  </button>
                  <button
                    onClick={() => setTaskFilter('completed')}
                    className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                      taskFilter === 'completed' ? 'bg-white text-green-600 shadow' : 'text-gray-600'
                    }`}
                  >
                    Завершенные ({tasks.filter(t => t.completed).length})
                  </button>
                  <button
                    onClick={() => setTaskFilter('all')}
                    className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                      taskFilter === 'all' ? 'bg-white text-gray-800 shadow' : 'text-gray-600'
                    }`}
                  >
                    Все ({tasks.length})
                  </button>
                </div>

                {/* Manager Filter - for admin/lead */}
                {(user?.role === 'admin' || user?.role === 'lead') && (
                  <div className="flex items-center space-x-4">
                    <label className="text-sm font-medium text-gray-700">Менеджер:</label>
                    <div className="flex items-center space-x-3">
                      {/* Quick buttons for common filters */}
                      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
                        <button
                          onClick={() => setManagerFilter('all')}
                          className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                            managerFilter === 'all' ? 'bg-white text-blue-600 shadow' : 'text-gray-600'
                          }`}
                        >
                          Все менеджеры
                        </button>
                        <button
                          onClick={() => setManagerFilter('my')}
                          className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                            managerFilter === 'my' ? 'bg-white text-purple-600 shadow' : 'text-gray-600'
                          }`}
                        >
                          Мои задачи
                        </button>
                      </div>
                      
                      {/* Dropdown for specific manager selection */}
                      <select
                        value={managerFilter}
                        onChange={(e) => setManagerFilter(e.target.value)}
                        className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-48"
                      >
                        <option value="all">Все менеджеры</option>
                        <option value="my">Мои задачи</option>
                        <option disabled>───────────</option>
                        {managers.map(manager => (
                          <option key={manager.id} value={manager.id}>
                            {manager.name} ({manager.role})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Content */}
          {activeSection === 'overdue-tasks' ? (
            <div className="bg-white rounded-lg shadow-md">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-red-800">🚨 Просроченные задачи</h3>
                <p className="text-sm text-gray-600">Задачи с истекшим сроком выполнения</p>
              </div>
              
              {tasks.filter(t => !t.completed && t.deadline && new Date(t.deadline) < new Date()).length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Проект</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Менеджер</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Задача</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Дедлайн</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Просрочено</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Действия</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {tasks.filter(t => !t.completed && t.deadline && new Date(t.deadline) < new Date()).map((task) => {
                        const daysOverdue = Math.floor((new Date() - new Date(task.deadline)) / (1000 * 60 * 60 * 24));
                        return (
                          <tr key={task.id} className="hover:bg-red-50">
                            <td className="px-6 py-4">
                              <Link to={`/project/${task.project_id}`} className="text-blue-600 hover:text-blue-800 font-medium">
                                {task.project_name}
                              </Link>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm font-medium text-gray-900">
                                {task.assigned_to_name || 'Не назначен'}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="font-medium">{task.title}</div>
                              <div className="text-xs text-gray-500">{task.task_type || 'Follow-up'}</div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-red-600 font-medium">
                                {new Date(task.deadline).toLocaleDateString('ru-RU')}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                {daysOverdue} {daysOverdue === 1 ? 'день' : daysOverdue < 5 ? 'дня' : 'дней'}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <button
                                onClick={() => openCompletionModal(task)}
                                className="text-green-600 hover:text-green-900 text-sm font-medium"
                              >
                                ✓ Завершить
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-green-600 mb-4">
                    <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900">Отлично! Нет просроченных задач</h3>
                  <p className="text-gray-500">Все задачи выполняются в срок</p>
                </div>
              )}
            </div>
          ) : activeSection === 'projects-without-tasks' ? (
            <div className="bg-white rounded-lg shadow-md">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-red-800">⚠️ Проекты без активных задач</h3>
                <p className="text-sm text-gray-600">Проекты, по которым менеджеры не ведут активную работу</p>
              </div>
              
              {projectsWithoutTasks.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs text-gray-500 uppercase">Проект</th>
                        <th className="px-6 py-3 text-left text-xs text-gray-500 uppercase">Менеджер</th>
                        <th className="px-6 py-3 text-left text-xs text-gray-500 uppercase">Дней без задач</th>
                        <th className="px-6 py-3 text-left text-xs text-gray-500 uppercase">Действия</th>
                      </tr>
                    </thead>
                    <tbody>
                      {projectsWithoutTasks.map((project) => (
                        <tr key={project.id} className="border-b hover:bg-red-50">
                          <td className="px-6 py-4">
                            <Link to={`/project/${project.id}`} className="text-blue-600 hover:text-blue-800">
                              {project.name}
                            </Link>
                          </td>
                          <td className="px-6 py-4">
                            {project.assigned_manager_name || 'Не назначен'}
                          </td>
                          <td className="px-6 py-4">
                            <span className={project.days_without_activity > 7 ? 'text-red-600 font-medium' : 'text-gray-600'}>
                              {project.days_without_activity} дн.
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <Link to={`/project/${project.id}`} className="text-blue-600 hover:text-blue-800 text-sm">
                              Открыть проект
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-green-600 mb-4">
                    <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900">Отлично! Все проекты имеют активные задачи</h3>
                  <p className="text-gray-500">Команда активно работает над всеми проектами</p>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Проект</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Менеджер</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Задача</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Дедлайн</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Комментарий</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Результат</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Действия</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredTasks.map((task) => (
                    <tr key={task.id} className={`hover:bg-gray-50 ${task.completed ? 'opacity-60' : ''}`}>
                      <td className="px-6 py-4">
                        <Link to={`/project/${task.project_id}`} className="text-blue-600 hover:text-blue-800 font-medium">
                          {task.project_name}
                        </Link>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {task.assigned_to_name || 
                           (task.project_assigned_manager ? 'Project Manager' : 'Не назначен')}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-medium">{task.title}</div>
                        <div className="text-xs text-gray-500">{task.task_type || 'Follow-up'}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className={`text-sm ${
                          task.deadline && new Date(task.deadline) < new Date() && !task.completed
                            ? 'text-red-600 font-medium' 
                            : task.completed ? 'text-green-600' : 'text-gray-900'
                        }`}>
                          {task.deadline ? new Date(task.deadline).toLocaleDateString('ru-RU') : 'Без срока'}
                          {task.completed && <span className="ml-1">✅</span>}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-600 max-w-xs">
                          {task.latest_comment ? (
                            <span title={task.latest_comment}>
                              {task.latest_comment.substring(0, 40)}{task.latest_comment.length > 40 ? '...' : ''}
                            </span>
                          ) : (
                            <span className="text-gray-400">Нет комментариев</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm max-w-xs">
                          {task.result ? (
                            <span title={task.result} className="text-green-700 font-medium">
                              {task.result.substring(0, 40)}{task.result.length > 40 ? '...' : ''}
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {!task.completed ? (
                          <button
                            onClick={() => openCompletionModal(task)}
                            className="text-green-600 hover:text-green-900 text-sm font-medium"
                          >
                            ✓ Завершить
                          </button>
                        ) : (
                          <span className="text-green-600 text-xs">✅ Выполнено</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {filteredTasks.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-gray-500">
                    {taskFilter === 'completed' ? 'Нет завершенных задач' : 
                     taskFilter === 'active' ? 'Нет активных задач' : 'Нет задач'}
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* Task Completion Modal */}
          {showCompletionModal && selectedTask && (
            <div className="modal-overlay" onClick={() => setShowCompletionModal(false)}>
              <div className="modal-content max-w-lg" onClick={e => e.stopPropagation()}>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Завершить задачу
                  </h3>
                  
                  <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-start space-x-3">
                      <div className="p-2 bg-blue-100 rounded">
                        <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-medium text-blue-900">{selectedTask.title}</p>
                        <p className="text-sm text-blue-700">{selectedTask.project_name}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    {/* Required Result */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <span className="text-red-600">*</span> Результат выполнения (обязательно)
                      </label>
                      <textarea
                        value={taskResult}
                        onChange={(e) => setTaskResult(e.target.value)}
                        placeholder="Обязательно опишите что было сделано, какой результат получен, с кем говорили..."
                        className={`form-textarea w-full ${!taskResult.trim() ? 'border-red-300 focus:border-red-500' : ''}`}
                        rows="4"
                        required
                      />
                      {!taskResult.trim() && (
                        <p className="text-red-600 text-xs mt-1">Поле обязательно для заполнения</p>
                      )}
                    </div>
                    
                    {/* Next Task */}
                    <div className="border-t border-gray-200 pt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Следующая задача (опционально)
                      </label>
                      <input
                        type="text"
                        value={nextTaskTitle}
                        onChange={(e) => setNextTaskTitle(e.target.value)}
                        placeholder="Что нужно сделать дальше по этому проекту..."
                        className="form-input w-full mb-3"
                      />
                      
                      {/* Next Task Deadline */}
                      {nextTaskTitle.trim() && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Срок выполнения следующей задачи
                          </label>
                          <input
                            type="date"
                            value={nextTaskDeadline}
                            onChange={(e) => setNextTaskDeadline(e.target.value)}
                            className="form-input w-full"
                            min={new Date().toISOString().split('T')[0]} // Today or future
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => setShowCompletionModal(false)}
                      className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded"
                    >
                      Отмена
                    </button>
                    <button
                      onClick={completeTask}
                      disabled={!taskResult.trim()}
                      className={`px-6 py-2 rounded font-medium transition-colors ${
                        taskResult.trim() 
                          ? 'bg-green-600 text-white hover:bg-green-700' 
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      {taskResult.trim() ? '✅ Завершить задачу' : 'Введите результат'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default TaskManagement;